import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { Api, ApiError, type RunInfo } from '../services/api'

/**
 * "Data updated X ago" badge + Refresh button.
 *
 * - Reads /runs/latest to know `data_updated_at` and whether a run is in flight.
 * - Refresh button POSTs /runs, then polls /runs/{id} every 2s until the
 *   status leaves "running". On success, invalidates the queries listed in
 *   `refetchKeys` so the page rerenders with the new data.
 */
interface Props {
  /** TanStack Query keys to invalidate after a successful run. */
  refetchKeys: ReadonlyArray<readonly unknown[]>
}

export function DataFreshness({ refetchKeys }: Props) {
  const qc = useQueryClient()

  const latest = useQuery({
    queryKey: ['runs', 'latest'],
    queryFn: Api.latestRun,
    // While a run is in flight we want to keep showing live status.
    refetchInterval: (q) => (q.state.data?.in_flight ? 3000 : false),
  })

  // Local poll target — set after we trigger a run.
  const [activeRunId, setActiveRunId] = useState<string | null>(
    latest.data?.in_flight?.run_id ?? null,
  )

  // If the server already knows about an in-flight run (page reload after
  // someone hit Refresh), pick it up automatically.
  useEffect(() => {
    if (!activeRunId && latest.data?.in_flight?.run_id) {
      setActiveRunId(latest.data.in_flight.run_id)
    }
  }, [activeRunId, latest.data?.in_flight?.run_id])

  const runStatus = useQuery({
    queryKey: ['runs', activeRunId],
    queryFn: () => Api.getRun(activeRunId!),
    enabled: !!activeRunId,
    refetchInterval: (q) => (q.state.data?.status === 'running' ? 2000 : false),
  })

  // When the in-flight run finishes, invalidate the consumer's queries.
  const lastSeenStatus = useRef<RunInfo['status'] | null>(null)
  useEffect(() => {
    const s = runStatus.data?.status
    if (!s) return
    const prev = lastSeenStatus.current
    lastSeenStatus.current = s
    if (prev === 'running' && (s === 'succeeded' || s === 'failed')) {
      void qc.invalidateQueries({ queryKey: ['runs', 'latest'] })
      for (const key of refetchKeys) {
        void qc.invalidateQueries({ queryKey: [...key] })
      }
    }
  }, [qc, refetchKeys, runStatus.data?.status])

  const trigger = useMutation({
    mutationFn: Api.triggerRun,
    onSuccess: (info) => {
      setActiveRunId(info.run_id)
      void qc.invalidateQueries({ queryKey: ['runs', 'latest'] })
    },
  })

  const inFlight =
    runStatus.data?.status === 'running' ||
    latest.data?.in_flight?.status === 'running' ||
    trigger.isPending

  const updatedAt = latest.data?.data_updated_at ?? null
  const triggerError =
    trigger.error instanceof ApiError ? trigger.error.message : trigger.error?.message

  return (
    <div className="flex flex-wrap items-center gap-3 text-sm">
      <FreshnessBadge updatedAt={updatedAt} loading={latest.isLoading} inFlight={inFlight} />

      {inFlight ? (
        <RunningPill info={runStatus.data ?? latest.data?.in_flight ?? null} />
      ) : (
        <button
          type="button"
          onClick={() => trigger.mutate()}
          className="btn-secondary gap-2"
          disabled={trigger.isPending}
        >
          <RefreshIcon /> Refresh data
        </button>
      )}

      {runStatus.data?.status === 'failed' && (
        <span className="rounded bg-rose-50 px-2 py-1 text-xs text-rose-700">
          Last run failed: {runStatus.data.error || `exit ${runStatus.data.returncode}`}
        </span>
      )}
      {triggerError && (
        <span className="rounded bg-rose-50 px-2 py-1 text-xs text-rose-700">
          {triggerError}
        </span>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Freshness badge with absolute-time tooltip
// ---------------------------------------------------------------------------
function FreshnessBadge({
  updatedAt,
  loading,
  inFlight,
}: {
  updatedAt: string | null
  loading: boolean
  inFlight: boolean
}) {
  // Re-render every 30s so the relative label drifts forward on its own.
  const [, force] = useState(0)
  useEffect(() => {
    const t = setInterval(() => force((n) => n + 1), 30_000)
    return () => clearInterval(t)
  }, [])

  if (loading && !updatedAt) {
    return <span className="text-slate-400">Checking freshness…</span>
  }
  if (!updatedAt) {
    // First-ever run still going (or no data): the in-flight pill carries
    // the status, so we just stay quiet here instead of saying "No data".
    return inFlight ? null : <span className="text-slate-400">No data yet.</span>
  }

  const date = new Date(updatedAt)
  const relative = relativeAge(date)
  const absolute = formatLocal(date)

  return (
    <span className="group relative inline-flex items-center gap-1.5 rounded-md bg-white px-2.5 py-1 text-slate-700 ring-1 ring-slate-200/80">
      <ClockIcon />
      <span>Data updated {relative}</span>
      <span className="invisible absolute left-1/2 top-full z-10 mt-2 -translate-x-1/2 whitespace-nowrap rounded bg-slate-900 px-2 py-1 text-xs text-white opacity-0 shadow-lg transition group-hover:visible group-hover:opacity-100">
        {absolute}
      </span>
    </span>
  )
}

function RunningPill({ info }: { info: RunInfo | null }) {
  return (
    <span className="inline-flex items-center gap-2 rounded-md bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700">
      <Spinner /> Refreshing data
      {info?.started_at && <span className="text-blue-500">· started {relativeAge(new Date(info.started_at))}</span>}
    </span>
  )
}

function ClockIcon() {
  return (
    <svg className="h-3.5 w-3.5 text-slate-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function RefreshIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 12a9 9 0 11-3.5-7.1L21 8" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M21 3v5h-5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function Spinner() {
  return (
    <svg className="h-3.5 w-3.5 animate-spin text-blue-500" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="4" />
      <path
        d="M4 12a8 8 0 018-8"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="4"
        fill="none"
      />
    </svg>
  )
}

// ---------------------------------------------------------------------------
// Time helpers
// ---------------------------------------------------------------------------
function relativeAge(d: Date): string {
  const seconds = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000))
  if (seconds < 5) return 'just now'
  if (seconds < 60) return `${seconds} seconds ago`
  const minutes = Math.floor(seconds / 60)
  if (minutes === 1) return '1 minute ago'
  if (minutes < 60) return `${minutes} minutes ago`
  const hours = Math.floor(minutes / 60)
  if (hours === 1) return '1 hour ago'
  if (hours < 24) return `${hours} hours ago`
  const days = Math.floor(hours / 24)
  if (days === 1) return '1 day ago'
  if (days < 30) return `${days} days ago`
  const months = Math.floor(days / 30)
  if (months === 1) return '1 month ago'
  return `${months} months ago`
}

function formatLocal(d: Date): string {
  // e.g. "Tue, May 6, 2026 at 8:35:14 PM PDT"
  const date = d.toLocaleDateString(undefined, {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
  const time = d.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short',
  })
  return `${date} at ${time}`
}
